import { createSelector } from '@reduxjs/toolkit';
import { OutletPrinter, PrinterState } from '../../types/printer';

interface RootState {
  printers: PrinterState;
}

const selectPrinterState = (state: RootState): PrinterState => state.printers;

export const selectOutletPrinters = createSelector(
  [selectPrinterState],
  (printerState) => printerState.outletPrinters,
);

export const selectInstalledPrinters = createSelector(
  [selectPrinterState],
  (printerState) => printerState.installedPrinters,
);

export const selectSectionPrinterMappings = createSelector(
  [selectPrinterState],
  (printerState) => printerState.sectionMappings,
);

export const selectTakeAwayPrinterMappings = createSelector(
  [selectPrinterState],
  (printerState) => printerState.takeAwayMappings,
);

export const selectPrinterDeviceId = createSelector(
  [selectPrinterState],
  (printerState) => printerState.deviceId,
);

export const selectPrintersLoading = createSelector(
  [selectPrinterState],
  (printerState) => printerState.isLoading,
);

export const selectPrintersSaving = createSelector(
  [selectPrinterState],
  (printerState) => printerState.isSaving,
);

export const selectPrintersTesting = createSelector(
  [selectPrinterState],
  (printerState) => printerState.isTesting,
);

export const selectPrintersError = createSelector(
  [selectPrinterState],
  (printerState) => printerState.error,
);

export const selectPrinterById = (printerId: string) =>
  createSelector([selectOutletPrinters], (printers) =>
    printers.find((printer) => printer.id === printerId),
  );

export const selectBillPrinterIdBySection = (sectionId: number) =>
  createSelector([selectSectionPrinterMappings], (mappings) => {
    const billMapping = mappings.find((mapping) =>
      mapping.section_id === sectionId
        ? mapping.operation === 'BILL' && !mapping.department_id
        : false,
    );

    return billMapping?.printer_id || null;
  });

export const selectDepartmentPrinterMapBySection = (sectionId: number) =>
  createSelector([selectSectionPrinterMappings], (mappings) => {
    return mappings
      .filter(
        (mapping) =>
          mapping.section_id === sectionId &&
          mapping.operation === 'KOT' &&
          Boolean(mapping.department_id),
      )
      .reduce<Record<string, string>>((acc, mapping) => {
        acc[mapping.department_id as string] = mapping.printer_id;
        return acc;
      }, {});
  });

export const selectSectionHasPrinterConfiguration = (sectionId: number) =>
  createSelector([selectSectionPrinterMappings], (mappings) =>
    mappings.some((mapping) => mapping.section_id === sectionId),
  );

export const selectBillPrinterIdByTakeAwayDevice = (deviceId: string) =>
  createSelector([selectTakeAwayPrinterMappings], (mappings) => {
    const billMapping = mappings.find(
      (mapping) =>
        mapping.device_id === deviceId &&
        mapping.operation === 'BILL' &&
        !mapping.department_id,
    );

    return billMapping?.printer_id || null;
  });

export const selectDepartmentPrinterMapByTakeAwayDevice = (deviceId: string) =>
  createSelector([selectTakeAwayPrinterMappings], (mappings) =>
    mappings
      .filter(
        (mapping) =>
          mapping.device_id === deviceId &&
          mapping.operation === 'KOT' &&
          Boolean(mapping.department_id),
      )
      .reduce<Record<string, string>>((acc, mapping) => {
        acc[mapping.department_id as string] = mapping.printer_id;
        return acc;
      }, {}),
  );

export const selectActivePrinters = createSelector(
  [selectOutletPrinters],
  (printers) => printers.filter((printer) => printer.is_active),
);

export const selectPrinterOptions = createSelector(
  [selectActivePrinters],
  (printers): { value: string; label: string; printer: OutletPrinter }[] =>
    printers.map((printer) => ({
      value: printer.id,
      label:
        printer.connection_type === 'network' && printer.ip_address
          ? `${printer.name} (${printer.connection_type} • ${printer.ip_address})`
          : `${printer.name} (${printer.connection_type})`,
      printer,
    })),
);
